import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import './cursor.css';

const INTERACTIVE_SELECTOR = [
  'button',
  '[role="button"]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
].join(',');

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FINE_POINTER_QUERY = '(pointer: fine)';
const ANY_FINE_POINTER_QUERY = '(any-pointer: fine)';
const HOVER_QUERY = '(hover: hover)';
const ANY_HOVER_QUERY = '(any-hover: hover)';
const TRAIL_MAX_POINTS = 16;
const TRAIL_POINT_MAX_AGE = 760;
const HEART_PATH = 'M48 81 C46.7 79.7 21 57.6 21 39.2 C21 27.8 28.3 20 38.2 20 C43.8 20 47 23.1 48 26.6 C49 23.1 52.2 20 57.8 20 C67.7 20 75 27.8 75 39.2 C75 57.6 49.3 79.7 48 81Z';

function getElementTarget(target) {
  if (target instanceof Element) {
    return target;
  }

  if (target instanceof Node) {
    return target.parentElement;
  }

  return null;
}

function isPinTarget(target) {
  const elementTarget = getElementTarget(target);

  if (!elementTarget) {
    return false;
  }

  const pinTarget = elementTarget.closest(INTERACTIVE_SELECTOR);

  if (pinTarget) {
    if (pinTarget instanceof HTMLButtonElement || pinTarget instanceof HTMLInputElement) {
      return !pinTarget.disabled && pinTarget.getAttribute('aria-disabled') !== 'true';
    }

    return pinTarget.getAttribute('aria-disabled') !== 'true';
  }

  const buttonLink = elementTarget.closest('a');

  if (!buttonLink || !buttonLink.querySelector(INTERACTIVE_SELECTOR)) {
    return false;
  }

  return buttonLink.getAttribute('aria-disabled') !== 'true';
}

function describeTarget(target) {
  const elementTarget = getElementTarget(target);

  if (!elementTarget) {
    return 'null';
  }

  const id = elementTarget.id ? `#${elementTarget.id}` : '';
  const className =
    typeof elementTarget.className === 'string' && elementTarget.className
      ? `.${elementTarget.className.trim().replace(/\s+/g, '.')}`
      : '';

  return `${elementTarget.tagName.toLowerCase()}${id}${className}`;
}

function buildTrailPath(points) {
  if (points.length < 2) {
    return '';
  }

  const format = (value) => Number(value).toFixed(1);
  let path = `M ${format(points[0].x)} ${format(points[0].y)}`;

  for (let index = 1; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    path += ` Q ${format(current.x)} ${format(current.y)} ${format(midX)} ${format(midY)}`;
  }

  const last = points[points.length - 1];
  path += ` L ${format(last.x)} ${format(last.y)}`;

  return path;
}

function StitchEffect({ effect }) {
  return (
    <div
      className="stitch-click-effect"
      style={{
        transform: `translate3d(${effect.x}px, ${effect.y}px, 0) translate3d(-50%, -50%, 0)`,
      }}
    >
      <svg className="stitch-dot" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <g className="stitch-dot__knot">
          <path d="M17 23.5 L31 23.5" stroke="#D8A6A0" strokeWidth="2.3" strokeLinecap="round" />
          <path d="M20 27 L28 21" stroke="#E8C891" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="24" cy="24" r="1.35" fill="#BCA66A" />
        </g>
      </svg>

      <svg className="heart-halo" viewBox="0 0 96 96" fill="none" aria-hidden="true">
        <defs>
          <radialGradient id="heartHaloWarmFill" cx="50%" cy="47%" r="58%">
            <stop offset="0%" stopColor="#FFF8EA" stopOpacity="0.95" />
            <stop offset="48%" stopColor="#F1D291" stopOpacity="0.58" />
            <stop offset="100%" stopColor="#E9B7A8" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="heartHaloRoseFill" cx="48%" cy="46%" r="64%">
            <stop offset="0%" stopColor="#FFF6EC" stopOpacity="0.5" />
            <stop offset="62%" stopColor="#EBC0B0" stopOpacity="0.36" />
            <stop offset="100%" stopColor="#EBC0B0" stopOpacity="0" />
          </radialGradient>
        </defs>
        <path className="heart-halo-wave heart-halo-wave--outer" d={HEART_PATH} fill="url(#heartHaloRoseFill)" />
        <path className="heart-halo-wave heart-halo-wave--inner" d={HEART_PATH} fill="url(#heartHaloWarmFill)" />
        <path className="heart-halo-core" d={HEART_PATH} fill="#FFE9BC" />
      </svg>
    </div>
  );
}

function createImmediateHalo(x, y) {
  const namespace = 'http://www.w3.org/2000/svg';
  const halo = document.createElement('span');
  halo.className = 'heart-click-ripple';
  halo.style.cssText = `
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    z-index: 2147483647;
    width: 104px;
    height: 104px;
    pointer-events: none;
    transform: translate3d(-50%, -50%, 0);
    mix-blend-mode: screen;
    filter:
      drop-shadow(0 0 7px rgba(245, 218, 158, 0.58))
      drop-shadow(0 0 18px rgba(232, 187, 174, 0.34))
      drop-shadow(0 0 30px rgba(255, 244, 218, 0.22));
  `;

  const svg = document.createElementNS(namespace, 'svg');
  svg.setAttribute('viewBox', '0 0 96 96');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText = 'display:block;width:100%;height:100%;overflow:visible;';

  const makePath = ({ fill, blur }) => {
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', HEART_PATH);
    path.setAttribute('fill', fill);
    path.style.transformBox = 'fill-box';
    path.style.transformOrigin = 'center';
    path.style.filter = `blur(${blur}px)`;
    svg.appendChild(path);
    return path;
  };

  const outer = makePath({ fill: 'rgba(232, 187, 174, 0.34)', blur: 4.5 });
  const middle = makePath({ fill: 'rgba(245, 218, 158, 0.3)', blur: 2.4 });
  const inner = makePath({ fill: 'rgba(245, 218, 158, 0.5)', blur: 1 });
  const core = makePath({ fill: 'rgba(255, 249, 236, 0.66)', blur: 0.4 });

  halo.appendChild(svg);

  document.body.appendChild(halo);

  outer.animate(
    [
      { opacity: 0, transform: 'scale(0.08)' },
      { opacity: 0.61, offset: 0.2 },
      { opacity: 0.26, transform: 'scale(0.82)', offset: 0.66 },
      { opacity: 0, transform: 'scale(1.04)' },
    ],
    { duration: 1220, easing: 'cubic-bezier(0.18, 0.84, 0.26, 1)', fill: 'forwards' }
  );

  middle.animate(
    [
      { opacity: 0, transform: 'scale(0.1)' },
      { opacity: 0.58, offset: 0.18 },
      { opacity: 0.2, transform: 'scale(0.76)', offset: 0.62 },
      { opacity: 0, transform: 'scale(0.96)' },
    ],
    { duration: 1080, delay: 40, easing: 'cubic-bezier(0.18, 0.84, 0.26, 1)', fill: 'forwards' }
  );

  inner.animate(
    [
      { opacity: 0, transform: 'scale(0.12)' },
      { opacity: 0.61, offset: 0.24 },
      { opacity: 0.26, transform: 'scale(0.64)', offset: 0.62 },
      { opacity: 0, transform: 'scale(0.82)' },
    ],
    { duration: 940, delay: 90, easing: 'cubic-bezier(0.18, 0.84, 0.26, 1)', fill: 'forwards' }
  );

  core.animate(
    [
      { opacity: 0, transform: 'scale(0.08)' },
      { opacity: 0.53, transform: 'scale(0.3)', offset: 0.28 },
      { opacity: 0, transform: 'scale(0.44)' },
    ],
    { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
  );

  halo.animate(
    [
      { opacity: 0.96 },
      { opacity: 0.9, offset: 0.58 },
      { opacity: 0 },
    ],
    { duration: 1280, easing: 'ease-out', fill: 'forwards' }
  );

  window.setTimeout(() => {
    halo.remove();
  }, 1300);
}

export default function CustomCursor({ forceEnabled = false, disabled = false }) {
  const cursorRef = useRef(null);
  const trailPathRef = useRef(null);
  const trailGlowPathRef = useRef(null);
  const frameRef = useRef(0);
  const releaseTimerRef = useRef(0);
  const trailPointsRef = useRef([]);
  const timersRef = useRef(new Set());
  const lastEffectRef = useRef({ time: 0, x: -1, y: -1 });
  const modeRef = useRef('idle');
  const hasMovedRef = useRef(false);
  const pointerRef = useRef({ x: 0, y: 0 });
  const cursorPositionRef = useRef({ x: 0, y: 0 });

  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('idle');
  const [visible, setVisible] = useState(false);
  const [viewport, setViewport] = useState({ width: 1, height: 1 });
  const [effects, setEffects] = useState([]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const finePointerMedia = window.matchMedia(FINE_POINTER_QUERY);
    const anyFinePointerMedia = window.matchMedia(ANY_FINE_POINTER_QUERY);
    const hoverMedia = window.matchMedia(HOVER_QUERY);
    const anyHoverMedia = window.matchMedia(ANY_HOVER_QUERY);
    const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

    const syncAvailability = () => {
      if (disabled) {
        setEnabled(false);
        return;
      }

      const touchPoints = navigator.maxTouchPoints ?? 0;
      const hasDesktopPointer =
        finePointerMedia.matches ||
        anyFinePointerMedia.matches ||
        hoverMedia.matches ||
        anyHoverMedia.matches ||
        touchPoints === 0;

      setEnabled(forceEnabled || (hasDesktopPointer && !reducedMotionMedia.matches));
    };

    syncAvailability();
    finePointerMedia.addEventListener('change', syncAvailability);
    anyFinePointerMedia.addEventListener('change', syncAvailability);
    hoverMedia.addEventListener('change', syncAvailability);
    anyHoverMedia.addEventListener('change', syncAvailability);
    reducedMotionMedia.addEventListener('change', syncAvailability);
    window.addEventListener('resize', syncAvailability);

    return () => {
      finePointerMedia.removeEventListener('change', syncAvailability);
      anyFinePointerMedia.removeEventListener('change', syncAvailability);
      hoverMedia.removeEventListener('change', syncAvailability);
      anyHoverMedia.removeEventListener('change', syncAvailability);
      reducedMotionMedia.removeEventListener('change', syncAvailability);
      window.removeEventListener('resize', syncAvailability);
    };
  }, [disabled, forceEnabled]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (!enabled) {
      document.documentElement.classList.remove('custom-cursor-active');
      setVisible(false);
      setMode('disabled');
      return undefined;
    }

    document.documentElement.classList.add('custom-cursor-active');
    setMode('idle');
    setViewport({ width: window.innerWidth, height: window.innerHeight });

    const startX = window.innerWidth / 2;
    const startY = window.innerHeight / 2;
    pointerRef.current = { x: startX, y: startY };
    cursorPositionRef.current = { x: startX, y: startY };
    const activeTimers = timersRef.current;

    const clearTrail = () => {
      trailPointsRef.current = [];

      if (trailPathRef.current) {
        trailPathRef.current.setAttribute('d', '');
      }

      if (trailGlowPathRef.current) {
        trailGlowPathRef.current.setAttribute('d', '');
      }
    };

    const pushTrailPoint = (x, y) => {
      const points = trailPointsRef.current;
      const lastPoint = points[points.length - 1];

      if (lastPoint && Math.hypot(x - lastPoint.x, y - lastPoint.y) < 5) {
        return;
      }

      points.push({ x, y, time: performance.now() });

      if (points.length > TRAIL_MAX_POINTS) {
        points.splice(0, points.length - TRAIL_MAX_POINTS);
      }

    };

    const createStitchEffect = (x, y, seed) => {
      const now = performance.now();
      const lastEffect = lastEffectRef.current;

      if (now - lastEffect.time < 180 && Math.hypot(x - lastEffect.x, y - lastEffect.y) < 8) {
        return;
      }

      lastEffectRef.current = { time: now, x, y };
      const id = `${seed}-${Math.round(x)}-${Math.round(y)}`;
      console.log('[custom-cursor] create halo', { id, x, y });
      createImmediateHalo(x, y);

      flushSync(() => {
        setEffects((currentEffects) => [...currentEffects.slice(-5), { id, x, y }]);
      });

      const timer = window.setTimeout(() => {
        setEffects((currentEffects) => currentEffects.filter((effect) => effect.id !== id));
        activeTimers.delete(timer);
      }, 1240);

      activeTimers.add(timer);
    };

    const animate = () => {
      const pointer = pointerRef.current;
      const cursorPosition = cursorPositionRef.current;
      const cursorElement = cursorRef.current;
      const isPinActive = modeRef.current === 'hover' || modeRef.current === 'down' || modeRef.current === 'release';
      const followStrength = isPinActive ? 0.78 : 0.42;

      cursorPosition.x += (pointer.x - cursorPosition.x) * followStrength;
      cursorPosition.y += (pointer.y - cursorPosition.y) * followStrength;

      if (cursorElement) {
        cursorElement.style.transform = `translate3d(${cursorPosition.x}px, ${cursorPosition.y}px, 0) translate3d(-50%, -50%, 0)`;
      }

      const now = performance.now();
      trailPointsRef.current = trailPointsRef.current.filter((point) => now - point.time < TRAIL_POINT_MAX_AGE);
      const path = buildTrailPath(trailPointsRef.current);

      if (trailPathRef.current) {
        trailPathRef.current.setAttribute('d', path);
      }

      if (trailGlowPathRef.current) {
        trailGlowPathRef.current.setAttribute('d', path);
      }

      frameRef.current = window.requestAnimationFrame(animate);
    };

    frameRef.current = window.requestAnimationFrame(animate);

    const updateHoverState = (target) => {
      if (modeRef.current === 'down') {
        return;
      }

      const isHovering = isPinTarget(target);
      setMode(isHovering ? 'hover' : 'idle');

      if (isHovering) {
        clearTrail();
      }
    };

    const handleClick = (event) => {
      if (event.button !== 0) {
        return;
      }

      console.log('[custom-cursor] click fallback', {
        x: event.clientX,
        y: event.clientY,
        eventTarget: describeTarget(event.target),
        eventTargetIsPin: isPinTarget(event.target),
      });

      createStitchEffect(event.clientX, event.clientY, event.timeStamp);
    };

    const handlePointerMove = (event) => {
      const previousPointer = pointerRef.current;
      pointerRef.current = { x: event.clientX, y: event.clientY };
      const isHovering = isPinTarget(event.target);

      if (!hasMovedRef.current) {
        hasMovedRef.current = true;
        setVisible(true);
      }

      if (isHovering || modeRef.current === 'down') {
        clearTrail();
      } else {
        pushTrailPoint(previousPointer.x, previousPointer.y);
        pushTrailPoint(event.clientX, event.clientY);
      }

      updateHoverState(event.target);
    };

    const handlePointerDown = (event) => {
      if (event.button !== 0) {
        return;
      }

      window.clearTimeout(releaseTimerRef.current);
      pointerRef.current = { x: event.clientX, y: event.clientY };
      setVisible(true);
      clearTrail();

      const target = document.elementFromPoint(event.clientX, event.clientY);
      const eventTargetIsPin = isPinTarget(event.target);
      const pointTargetIsPin = isPinTarget(target);

      console.log('[custom-cursor] press', {
        type: event.type,
        x: event.clientX,
        y: event.clientY,
        eventTarget: describeTarget(event.target),
        pointTarget: describeTarget(target),
        eventTargetIsPin,
        pointTargetIsPin,
      });

      createStitchEffect(event.clientX, event.clientY, event.timeStamp);

      if (eventTargetIsPin || pointTargetIsPin) {
        setMode('down');
      } else {
        setMode('idle');
      }
    };

    const handlePointerUp = (event) => {
      if (event.button !== 0) {
        return;
      }

      pointerRef.current = { x: event.clientX, y: event.clientY };

      const target = document.elementFromPoint(event.clientX, event.clientY);
      const releasedOnInteractive = isPinTarget(target);
      const nextMode = releasedOnInteractive ? 'hover' : 'idle';
      window.clearTimeout(releaseTimerRef.current);

      if (modeRef.current === 'down') {
        setMode('release');
        releaseTimerRef.current = window.setTimeout(() => {
          setMode(nextMode);
        }, 180);
      } else {
        setMode(nextMode);
      }
    };

    const handleMouseLeave = () => {
      setVisible(false);
      setMode('idle');
      clearTrail();
    };

    const handleMouseEnter = () => {
      if (hasMovedRef.current) {
        setVisible(true);
      }
    };

    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      clearTrail();
    };

    window.addEventListener('mousemove', handlePointerMove, { passive: true });
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerdown', handlePointerDown, { capture: true });
    window.addEventListener('mousedown', handlePointerDown, { capture: true });
    window.addEventListener('pointerup', handlePointerUp, { capture: true });
    window.addEventListener('mouseup', handlePointerUp, { capture: true });
    window.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('resize', handleResize);
    document.documentElement.addEventListener('mouseleave', handleMouseLeave);
    document.documentElement.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      document.documentElement.classList.remove('custom-cursor-active');
      window.cancelAnimationFrame(frameRef.current);
      window.clearTimeout(releaseTimerRef.current);
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      window.removeEventListener('mousedown', handlePointerDown, { capture: true });
      window.removeEventListener('pointerup', handlePointerUp, { capture: true });
      window.removeEventListener('mouseup', handlePointerUp, { capture: true });
      window.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('resize', handleResize);
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
      document.documentElement.removeEventListener('mouseenter', handleMouseEnter);
      activeTimers.forEach((timer) => window.clearTimeout(timer));
      activeTimers.clear();
    };
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  const cursorClassName = [
    'custom-cursor',
    visible ? 'is-visible' : '',
    mode === 'hover' ? 'is-hover' : '',
    mode === 'down' ? 'is-down' : '',
    mode === 'release' ? 'is-release' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <svg
        className="custom-cursor-trail"
        viewBox={`0 0 ${viewport.width} ${viewport.height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path ref={trailGlowPathRef} className="custom-cursor-trail__glow" />
        <path ref={trailPathRef} className="custom-cursor-trail__path" />
      </svg>

      <div ref={cursorRef} className={cursorClassName} aria-hidden="true">
        <span className="custom-cursor__point" />
        <svg className="custom-cursor__pin" viewBox="0 0 96 96" fill="none">
          <g className="custom-cursor__pin-glyph">
            <path
              className="custom-cursor__pin-soft-shadow"
              d="M75 2 L48 48"
              stroke="#1E2A47"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <path
              d="M73 1 L48 48"
              stroke="#1E2A47"
              strokeWidth="2.45"
              strokeLinecap="round"
            />
            <circle cx="73" cy="1" r="8.8" fill="#BCA66A" stroke="#7D5E25" strokeWidth="1.4" />
            <circle cx="73" cy="1" r="3.7" fill="#EAD9CF" stroke="#D8A6A0" strokeWidth="1" />
            <path
              d="M67 11 L50.2 44.7"
              stroke="#EAD9CF"
              strokeWidth="0.9"
              strokeLinecap="round"
              opacity="0.42"
            />
          </g>
        </svg>
      </div>

      <div className="stitch-effect-layer" aria-hidden="true">
        {effects.map((effect) => (
          <StitchEffect key={effect.id} effect={effect} />
        ))}
      </div>
    </>
  );
}
